// Realtime LIVE strip — ported from legacy scheduleReload/setLiveStatus/
// setupRealtime (v20 lines ~1204-1256).
//
// ONE channel subscribes to postgres_changes on the four hot tables. Events
// do not reload anything directly: each event flags which data keys are dirty
// and arms a single trailing debounce timer, so a burst of changes publishes
// ONE coalesced notification ~1s after the last event (the final update is
// never dropped). Transport state ({status}) lives on SLICES.REALTIME; the
// coalesced batches ({changed, at}) publish on SLICES.RT_CHANGES — a SEPARATE
// slice so a chip flap (live→connecting→live) can never re-deliver a stale
// batch to Phase 2 loaders. Loaders subscribe to RT_CHANGES and refetch the
// keys they own. Toasts stay immediate.
//
// The Live chip reflects the REAL channel state (legacy A5: only 'Live'
// while SUBSCRIBED), and a RECOVERY (error/closed → subscribed) replays all
// keys because realtime only streams FUTURE changes — rows that changed
// while disconnected are otherwise missed until a manual sync.

import { store, SLICES } from './state/store.js';
import { getClient } from './db/client.js';
import { TABLES } from './db/contract.js';
import { toast } from './ui/toast.js';

export const RT_DEBOUNCE_MS = 1000; // legacy RT_DEBOUNCE_MS
export const CONSENT_TOAST_WINDOW_MS = 5000; // legacy consent-INSERT toast rate limit

// Data keys flagged dirty per table (legacy loader lists, by name not fn):
// consents→intake; adverse_events→safety; cases→patients+inbox+pipeline;
// tasks→inbox. Phase 2 loaders subscribe to the RT_CHANGES slice and refetch
// the keys they own.
export const DIRTY_BY_TABLE = Object.freeze({
  [TABLES.CONSENTS]: Object.freeze(['intake']),
  [TABLES.ADVERSE_EVENTS]: Object.freeze(['safety']),
  [TABLES.CASES]: Object.freeze(['patients', 'inbox', 'pipeline']),
  [TABLES.TASKS]: Object.freeze(['inbox']),
});

// Every key — replayed after a reconnect (legacy line 1249's full loader list).
export const ALL_KEYS = Object.freeze(['inbox', 'intake', 'safety', 'patients', 'pipeline']);

// ─── Pure: channel status → chip state (legacy subscribe callback) ─────────

/**
 * Map a supabase channel status to the Live-chip state. Statuses outside the
 * legacy set (e.g. 'SUBSCRIBING' mid-handshake) return null = leave the chip
 * unchanged, exactly as the legacy if/else chain fell through silently.
 * @param {string} status supabase channel status
 * @returns {'live'|'connecting'|'offline'|null}
 */
export function liveStateFor(status) {
  if (status === 'SUBSCRIBED') return 'live';
  if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') return 'connecting';
  if (status === 'CLOSED') return 'offline';
  return null;
}

/**
 * Should a fresh SUBSCRIBED replay the loaders? Only on RECOVERY from a
 * broken state — first connect (prev undefined) stays quiet (legacy 1244-48).
 * @param {string|undefined} prev previous channel status
 * @param {string} status new channel status
 * @returns {boolean}
 */
export function shouldReplay(prev, status) {
  return (
    status === 'SUBSCRIBED' &&
    (prev === 'CHANNEL_ERROR' || prev === 'TIMED_OUT' || prev === 'CLOSED')
  );
}

/**
 * Rate limit for the consent-INSERT toast: one neutral toast per 5s window
 * (bulk imports and the admin's own enrolment insert fire the event too).
 * Strictly-greater comparison, as legacy (`_n-(window._lastConsentToast||0)>5000`).
 * @param {number} last epoch ms of the last toast (0 = never)
 * @param {number} now
 * @returns {boolean}
 */
export function consentToastDue(last, now) {
  return now - (last || 0) > CONSENT_TOAST_WINDOW_MS;
}

// ─── Pure-ish: trailing-debounce coalescer (legacy scheduleReload) ─────────

/**
 * Create a coalescer: schedule(...keys) collects keys into a Set and (re)arms
 * ONE trailing timer; when it fires, flush(keys[]) runs once with everything
 * collected since the last flush. Exact legacy semantics: every schedule
 * call clears and re-arms the timer (trailing edge), the dirty set is
 * cleared BEFORE flush so keys scheduled during flush start a new batch, and
 * a flush failure never breaks the coalescer.
 * @param {(keys: string[]) => void} flush
 * @param {{debounceMs?: number, setTimeout?: any, clearTimeout?: any}} [opts] injectable timers for tests
 * @returns {{schedule: (...keys: string[]) => void, pending: () => string[]}}
 */
export function createCoalescer(flush, opts) {
  const debounceMs = (opts && opts.debounceMs) ?? RT_DEBOUNCE_MS;
  const setT = (opts && opts.setTimeout) || globalThis.setTimeout;
  const clearT = (opts && opts.clearTimeout) || globalThis.clearTimeout;
  /** @type {Set<string>} */
  const dirty = new Set();
  /** @type {any} */
  let timer = null;
  return {
    schedule(...keys) {
      for (const k of keys) dirty.add(k);
      clearT(timer);
      timer = setT(() => {
        const due = [...dirty];
        dirty.clear();
        timer = null;
        try {
          flush(due);
        } catch (e) {
          console.error('realtime reload failed', e);
        }
      }, debounceMs);
    },
    /** Test/inspection: keys currently awaiting flush. */
    pending() {
      return [...dirty];
    },
  };
}

// ─── Chip renderer (legacy setLiveStatus) ──────────────────────────────────

// Chip copy + title per state — legacy wording, INCLUDING the offline tip that
// names Sync (v20 refreshData, line 526). Phase 1 shipped the chip without the
// button, so the tip had to fall back to "reload the page"; F-NAV-03 put the
// Sync control back in the top bar at every breakpoint, so the tip once again
// names the cheap remedy instead of a full bundle re-download.
// Exported so the Help modal reports the SAME connection vocabulary.
export const LIVE_CHIP_COPY = {
  live: { text: 'Live', title: 'Realtime connection active' },
  connecting: {
    text: 'Reconnecting…',
    title: 'Realtime connection lost — reconnecting. Data may be stale.',
  },
  offline: {
    text: 'Offline',
    title: 'Realtime connection offline — dashboard may be stale. Use Sync to refresh.',
  },
};

function renderChip() {
  const doc = /** @type {any} */ (globalThis).document;
  const slot = doc && doc.getElementById('live-slot');
  if (!slot) return;
  const rt = store.get(SLICES.REALTIME) || {};
  const state = /** @type {'live'|'connecting'|'offline'} */ (rt.status) || 'connecting';
  const c = LIVE_CHIP_COPY[state] || LIVE_CHIP_COPY.connecting;
  slot.innerHTML =
    `<span class="live ${state}" title="${c.title}">` +
    '<span class="ldot" aria-hidden="true"></span>' +
    `<span class="live-text">${c.text}</span></span>`;
}

// ─── Wiring (legacy setupRealtime) ─────────────────────────────────────────

/** @type {string|undefined} */
let _prevStatus = undefined;
let _lastConsentToast = 0;
let _wired = false;

/** Test-only: reset module wiring state. @returns {void} */
export function _resetRealtime() {
  _prevStatus = undefined;
  _lastConsentToast = 0;
  _wired = false;
}

/**
 * Subscribe the single realtime channel and drive the Live chip (REALTIME
 * slice) + coalesced change batches (RT_CHANGES slice). Call once after
 * mountShell().
 * @param {{client?: any, timers?: {setTimeout?: any, clearTimeout?: any}}} [deps] test injection
 * @returns {void}
 */
export function setupRealtime(deps) {
  const client = (deps && deps.client) || getClient();

  if (!_wired) {
    _wired = true;
    store.subscribe(SLICES.REALTIME, renderChip);
  }
  store.set(SLICES.REALTIME, { status: 'connecting' });

  const coalescer = createCoalescer((keys) => {
    // ONE publication per burst, on its OWN slice: REALTIME status writes
    // shallow-merge, so publishing {changed} there would leave the batch
    // attached forever and every chip flap would re-deliver it to loaders.
    store.set(SLICES.RT_CHANGES, { changed: keys, at: Date.now() });
  }, deps && deps.timers);

  client
    .channel('rt')
    // Bind the intake feed to the consents TABLE — consent_submission is a
    // VIEW and views never emit postgres_changes (legacy fix, kept).
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: TABLES.CONSENTS },
      (/** @type {any} */ p) => {
        if (p.eventType === 'INSERT') {
          const now = Date.now();
          if (consentToastDue(_lastConsentToast, now)) {
            _lastConsentToast = now;
            toast('New consent received', 'ok');
          }
        }
        coalescer.schedule(...DIRTY_BY_TABLE[TABLES.CONSENTS]);
      }
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: TABLES.ADVERSE_EVENTS },
      (/** @type {any} */ p) => {
        if (p.eventType === 'INSERT') toast('New adverse event reported', 'err');
        coalescer.schedule(...DIRTY_BY_TABLE[TABLES.ADVERSE_EVENTS]);
      }
    )
    .on('postgres_changes', { event: '*', schema: 'public', table: TABLES.CASES }, () => {
      coalescer.schedule(...DIRTY_BY_TABLE[TABLES.CASES]);
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: TABLES.TASKS }, () => {
      coalescer.schedule(...DIRTY_BY_TABLE[TABLES.TASKS]);
    })
    .subscribe((/** @type {string} */ status) => {
      const prev = _prevStatus;
      _prevStatus = status;
      const state = liveStateFor(status);
      if (state) store.set(SLICES.REALTIME, { status: state });
      if (shouldReplay(prev, status)) {
        // Recovery: replay everything — changes made while disconnected were
        // never streamed (legacy 1249) — through the SAME debounce.
        coalescer.schedule(...ALL_KEYS);
        toast('Reconnected — refreshing', 'ok');
      }
    });
}
